'use client';

import type { UrlObject } from 'node:url';
import LoadingLink from '@/components/ui/LoadingLink/LoadingLink';
import type { Route } from 'next';
import styles from './ActionButton.module.scss';

function ActionButton<T extends string>({
  children,
  onClick,
  href,
  type = 'button',
  disabled = false,
  size = 'default',
  color = 'black',
  bgColor = 'white',
  outlined = false,
  borderColor,
  isLoading = false,
  dataTestId,
  isShadow = false,
  noDisabledOpacity = false,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  href?: Route<T> | UrlObject;
  type?: 'button' | 'submit';
  disabled?: boolean;
  size?:
    | 'xSmall'
    | 'small'
    | 'default'
    | 'large'
    | 'spCompact'
    | 'xLarge'
    | 'xxLarge'
    | 'fullWidth'
    | 'fullWidthLarge'
    | 'fullWidthMedium'
    | 'fullWidthSmall';
  /** 参照: src/styles/partials/_variables.scss */
  color?: string;
  bgColor?: string;
  outlined?: boolean;
  /** 参照: src/styles/partials/_variables.scss - outlinedがtrueの場合の枠線の色（未指定の場合はbgColorを使用） */
  borderColor?: string;
  isLoading?: boolean;
  dataTestId?: string;
  isShadow?: boolean;
  /**
   * disabled時のopacity: 0.3を適用しない。
   * デフォルトのdisabled表現はopacity: 0.3だが、Figmaデザインで背景色だけ変えて
   * 文字の透明度は変えたくないケースがある。その場合にtrueにして、
   * 呼び出し側のbgColor/color propsで非活性スタイルを制御する。
   */
  noDisabledOpacity?: boolean;
}) {
  const isClickable = !disabled && !isLoading;

  const handleClick = () => {
    if (isLoading) {
      return;
    }

    onClick?.();
  };

  return (
    <>
      {href ? (
        <LoadingLink
          href={href}
          className={`${styles.actionButton} ${styles[size]} ${!isClickable && !noDisabledOpacity ? styles.disableOpacity : ''} ${isShadow ? styles.shadow : ''}`}
          style={generateStyle({ color, bgColor, outlined, borderColor })}
          data-testid={dataTestId}
        >
          {children}
        </LoadingLink>
      ) : (
        <button
          className={`${styles.actionButton} ${styles[size]} ${!isClickable && !noDisabledOpacity ? styles.disableOpacity : ''}`}
          type={type}
          onClick={handleClick}
          style={generateStyle({ color, bgColor, outlined, borderColor })}
          disabled={!isClickable}
          data-testid={dataTestId}
        >
          {isLoading ? <div className={styles.loader} /> : children}
        </button>
      )}
    </>
  );
}

const generateStyle = ({
  color,
  bgColor,
  outlined,
  borderColor,
}: {
  color?: string;
  bgColor?: string;
  outlined?: boolean;
  borderColor?: string;
}): React.CSSProperties => {
  if (outlined) {
    const borderColorValue = borderColor ?? bgColor;
    return {
      color: `var(--color-${color})`,
      border: `1px solid var(--color-${borderColorValue})`,
      background: `var(--color-${bgColor})`,
    };
  }
  return {
    color: `var(--color-${color})`,
    background: `var(--color-${bgColor})`,
  };
};

export default ActionButton;
